import { CommonComponent } from './../components/shared/common/common.component';
import {EventEmitter, Injectable} from '@angular/core';
import {HttpClient} from '@angular/common/http';
import {AccountConfiguration, ApplicationConfiguration, HomeConfigurations} from '../domain/configurations';
import {LocalStorageService} from 'angular-2-local-storage';
import {StorageItem} from '../enums/storage-item';
import {map, mergeMap} from 'rxjs/operators';
import { ReactiveService } from './reactive.service';
import { Router } from '@angular/router';
import { processMaintenanceText } from '../components/shared/common/global-functions';
import {TranslateService} from '@ngx-translate/core';

@Injectable({
  providedIn: 'root'
})
export class ConfigurationService {
  home: HomeConfigurations = this.localStorage.get(StorageItem.CONFIGS_HOME) ?? new HomeConfigurations();
  account: AccountConfiguration = this.localStorage.get(StorageItem.CONFIGS_ACCOUNT) ?? new AccountConfiguration();
  application: ApplicationConfiguration = this.localStorage.get(StorageItem.CONFIGS_APPLICATION) ?? new ApplicationConfiguration();
  configReceive = new EventEmitter<any>();

  isMaintananceMode = true;

  isPronounsOnboarding = false;
  isPronounsWelcome = false;

  constructor(private http: HttpClient, private localStorage: LocalStorageService, public reactiveService: ReactiveService, public router: Router, public translate: TranslateService) {}

  clearConfigurations() {
    this.localStorage.remove(StorageItem.CONFIGS_HOME, StorageItem.CONFIGS_ACCOUNT, StorageItem.CONFIGS_APPLICATION);
    this.home = new HomeConfigurations();
    this.account = new AccountConfiguration();
    this.application = new ApplicationConfiguration();
  }

  getPlatformConfigService() {
    return this.http.get(`/configurations`).pipe(
      map(
        (response: any) => {

          this.home.contactEmail = response.CONTACT_EMAIL ?? this.home.contactEmail;
          this.home.urlAndroidStore = response.URL_ANDROID_STORE ?? this.home.urlAndroidStore;
          this.home.urlIosStore = response.URL_IOS_STORE ?? this.home.urlIosStore;
          this.home.footerDisclaimerEn = response.FOOTER_CUSTOM_DISCLAIMER_EN ?? null;
          this.home.footerDisclaimerEs = response.FOOTER_CUSTOM_DISCLAIMER_ES ?? null;
          this.localStorage.set(StorageItem.CONFIGS_HOME, this.home);

          this.home.contractsData = response.CONTRACTS_DATA ?? this.home.contractsData;

          this.home.maintenanceMode = response.ENABLE_MAINTENANCE_MODE ?? this.isMaintananceMode;
          this.home.maintenanceText = response.MAINTENANCE_MODE_TEXT ?? this.home.maintenanceText;

          this.home.pronounsOnboarding = response.ENABLE_ONBOARDING_PRONOUNS ?? this.isPronounsOnboarding;
          this.home.pronounsWelcome = response.ENABLE_WELCOME_PRONOUNS ?? this.isPronounsWelcome;


          let languageObject = processMaintenanceText(response.MAINTENANCE_MODE_TEXT);
            this.setTextObjectOffline(languageObject);

          this.home.textFaqs = response.FAQS_CONTENT_TEXT_DATA;
          this.home.textFaqsWithoutCard = response.FAQS_CONTENT_TEXT_DATA_WITHOUT_CARD_PURCHASE;

          this.account.cardPrice = response.CARD_PRICE ?? this.account.cardPrice;
          this.account.statementsMaxDaysQueryPeriod = response.STATEMENTS_MAX_DAYS_QUERY_PERIOD ??
            this.account.statementsMaxDaysQueryPeriod;
          this.account.hasCardPurchase = response.HAS_CARD_PURCHASE ?? this.account.hasCardPurchase;
          this.account.hasCardLoads = response.HAS_CARD_LOADS ?? this.account.hasCardLoads;
          this.account.hasStatements = response.HAS_STATEMENTS ?? this.account.hasStatements;
          this.account.hasCardBlocking = response.HAS_CARD_BLOCKING ?? this.account.hasCardBlocking;
          this.account.enablePinValidate = response.ENABLE_PIN_VALIDATE ?? this.account.enablePinValidate;
          this.account.enablePinChange = response.ENABLE_PIN_CHANGE ?? this.account.enablePinChange;
          this.account.enablePinReset = response.ENABLE_PIN_RESET ?? this.account.enablePinReset;
          this.account.abaRoutingNumber = response.ABA_ROUTING_NUMBER ?? this.account.abaRoutingNumber;
          this.account.contactEmail = response.CONTACT_EMAIL ?? this.account.contactEmail;
          this.account.callCenterNumber = response.CALL_CENTER_NUMBER ?? this.account.callCenterNumber;
          this.account.enableInternalTransfers = response.ENABLE_INTERNAL_TRANSFERS ?? this.account.enableInternalTransfers;
          this.account.enableInternationalTransfers = response.ENABLE_INTERNATIONAL_TRANSFERS ??
            this.account.enableInternationalTransfers;
          this.account.enableAccountCancellation = response.ENABLE_ACCOUNT_CANCELLATION ??
            this.account.enableAccountCancellation;
          this.account.enableFamilyHealth = response.FAMILY_HEALTH_ENABLE ?? this.account.enableFamilyHealth;
          this.account.enableSelfieDocuments = response.ENABLE_SELFIE_UPLOAD_FILES ?? this.account.enableSelfieDocuments;
          this.account.enableShowFaqsScreen = response.ENABLE_SHOW_FAQS_SCREEN ?? this.account.enableShowFaqsScreen;
          this.account.verificationProcessTime = response.VERIFICATION_PROCESS_TIME ?? this.account.verificationProcessTime;
          this.account.minGreendotCashLoads = response.MIN_GREENDOT_CASH_LOADS ?? this.account.minGreendotCashLoads;
          this.account.maxGreendotCashLoads = response.MAX_GREENDOT_CASH_LOADS ?? this.account.maxGreendotCashLoads;
          this.account.cardPurchaseType = response.CARD_PURCHASE_TYPE ?? this.account.cardPurchaseType;
          // this.account.enableGeolocation = gatewayResponse.ENABLE_GEOLOCATION ?? this.account.enableGeolocation;
          this.localStorage.set(StorageItem.CONFIGS_ACCOUNT, this.account);

          this.application.onboardingEnable = response.ONBOARDING_WEB_ENABLE ?? this.application.onboardingEnable;
          this.application.brokerEnabled = response.BROKER_ENABLED ?? this.application.brokerEnabled;
          this.application.enforceAgentLinkCorrectness = response.ENABLE_ENFORCE_AGENT_LINK_CORRECTNESS ??
            this.application.enforceAgentLinkCorrectness;
          this.application.brokerEnabledInviteFriend = response.ENABLE_BROKER_INVITE_FRIEND ??
            this.application.brokerEnabledInviteFriend;
          this.application.validateCurp = response.VALIDATE_CURP ?? this.application.validateCurp;
          this.application.instantIssue = response.INSTANT_ISSUE ?? this.application.instantIssue;
          this.application.enablePrivacyAct = response.ENABLE_PRIVACY_ACT ?? this.application.enablePrivacyAct;
          this.application.enablePhoneUsa = response.ENABLE_PHONE_USA ?? this.application.enablePhoneUsa;
          this.application.enablePhoneMex = response.ENABLE_PHONE_MEX ?? this.application.enablePhoneMex;
          this.application.enablePhoneGtm = response.ENABLE_PHONE_GTM ?? this.application.enablePhoneGtm;
          this.application.enablePhoneEcu = response.ENABLE_PHONE_ECU ?? this.application.enablePhoneEcu;
          this.application.enablePhoneSlv = response.ENABLE_PHONE_SLV ?? this.application.enablePhoneSlv;

          this.application.enableSelfieRequiered = response.ENABLE_SELFIE_AMERICAN_REQUIRED ??
            this.application.enableSelfieRequiered;
          this.application.externalReferenceCodeMaxLength = response.EXTERNAL_REFERENCE_CODE_MAX_LENGTH ??
            this.application.externalReferenceCodeMaxLength;
          this.application.maxTimeToWaitCardCreateInMillis = response.MAX_TIME_TO_WAIT_CARD_CREATE_IN_MILLIS ??
            this.application.maxTimeToWaitCardCreateInMillis;
          this.application.sendBrokerScreenToEnd = response.SEND_BROKER_SCREEN_TO_END ?? this.application.sendBrokerScreenToEnd;
          this.application.guatemalaDpiInputEnable = response.GUATEMALA_DPI_INPUT_ENABLE ??
            this.application.guatemalaDpiInputEnable;
          this.application.secondAddressLineEnable = response.SECOND_ADDRESS_LINE_ENABLE ??
            this.application.secondAddressLineEnable;

          this.localStorage.set(StorageItem.CONFIGS_APPLICATION, this.application);
          this.onConfigReceive();

          this.reactiveService.updateRequestCompletedValue(true);

        }, error => {
        }
      )
    );
  }

  getPlatformConfig() {
    return this.getPlatformConfigService().subscribe();
  }

  onConfigReceive() {
    this.configReceive.emit(null);
  }

  setTextObjectOffline(object: any){

    if (this.translate.currentLang === 'es' && object.es && object.es.length > 0) {
      this.reactiveService.setReactiveText(object.es[0]);

    } else if (this.translate.currentLang === 'en' && object.en && object.en.length > 0) {
      this.reactiveService.setReactiveText(object.en[0]);

    } else {
      console.warn('No se encontró texto para el idioma actual:', this.translate.currentLang);
    }

  }

}
